import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Text } from 'foundations-components/components/Text';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { StepperInput } from 'foundations-components/transitional/components/StepperInput';
import { CustomError } from 'pricing-pages-lib/errors/CustomErrors';
import { getI18nText } from 'pricing-pages-lib/utils/languageUtils';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { PriceDisplay } from 'pricing-pages-ui-components/price/PriceDisplay';
import PurchaseMotionButtonWrapper from 'pricing-pages-ui/components/common/purchaseMotions/PurchaseMotionButtonWrapper';
import { useState } from 'react';
import { HUBSPOT_CREDITS } from 'self-service-api/constants/UnitNames';
import { BUY_TOUCHLESS } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import { APP } from '../../constants/tracking/TrackingSources';
import { useGlobalLoading } from '../../hooks/loadingHooks';
import { useCalculatedPrice } from '../../hooks/useBulkPurchaseOptions';
import { useDisplayedCurrencyCode } from '../../hooks/useCurrencies';
import { useTrackingFlow } from '../../hooks/useTrackingFlow';
import { useAppSelector } from '../../reducers';
import { checkoutProductConfigurationsSelector } from '../../selectors/ProductConfigsSelectors';
import { ownedHubSpotCreditsSkuSelector, ownedHubSpotCreditsTotalSelector, productsFromProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { includedQuantityBySkuIdSelector, productVersionSelector, upgradeProductSelector } from '../../selectors/ProductsSelectors';
import { purchaseMotionsSelector } from '../../selectors/PurchaseMotionSelectors';
import { trackAdditionalUnitsPicker, trackBuyButtonClick, trackHoverInteraction, trackTalkToSalesClick } from '../../utils/tracking/trackingEventUtils';
import { useQueryParamTerm } from '../../utils/urls/appQueryParamUtils';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import withErrorBoundary from '../error-boundary/withErrorBoundary';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BuyMoreCredits = ({
  screen,
  trackingSource
}) => {
  const isAppReady = useGlobalLoading();
  const ownedQuantity = useAppSelector(ownedHubSpotCreditsTotalSelector);
  const [selectedQuantity, setSelectedQuantity] = useState(ownedQuantity || 1);
  const ownedHubSpotCreditsSkuId = useAppSelector(ownedHubSpotCreditsSkuSelector);
  const ownedHubSpotCreditsVersion = useAppSelector(state => {
    if (!ownedHubSpotCreditsSkuId) return null;
    return productVersionSelector(state, {
      skuId: ownedHubSpotCreditsSkuId
    });
  });
  const apiName = ownedHubSpotCreditsVersion === null || ownedHubSpotCreditsVersion === void 0 ? void 0 : ownedHubSpotCreditsVersion.productApiName;
  const includedUnits = useAppSelector(state => {
    if (!ownedHubSpotCreditsSkuId) {
      return null;
    }
    return includedQuantityBySkuIdSelector(state, {
      skuId: ownedHubSpotCreditsSkuId,
      unitName: HUBSPOT_CREDITS
    });
  });
  const productConfigurations = useAppSelector(state => checkoutProductConfigurationsSelector(state, {
    productConfigurations: ownedHubSpotCreditsSkuId ? [createProductConfig({
      skuId: ownedHubSpotCreditsSkuId,
      quantity: selectedQuantity || 1
    })] : []
  }));
  const maxTouchlessPurchasableQuantity = ownedHubSpotCreditsVersion ? ownedHubSpotCreditsVersion.maxTouchlessPurchasableQuantity : 0;
  const minQuantityForStepper = ownedQuantity;
  const maxQuantityForStepper = maxTouchlessPurchasableQuantity === null ? Infinity : maxTouchlessPurchasableQuantity;
  const ownedProducts = useAppSelector(state => productsFromProductInventorySelector(state).map(product => product.apiName));
  const purchaseMotionLocation = 'CALCULATOR';
  const purchaseMotions = useAppSelector(state => {
    if (!isAppReady || !ownedHubSpotCreditsSkuId) return [];
    return purchaseMotionsSelector(state, {
      skuId: ownedHubSpotCreditsSkuId,
      location: purchaseMotionLocation
    });
  });
  const purchaseMotion = purchaseMotions[0];
  const isTouchlessPurchase = purchaseMotion === BUY_TOUCHLESS;

  // disable the button if not touchless or the user hasn't increased the stepper to more than already owned
  const disabledButton = isTouchlessPurchase &&
  // only disable if touchless purchase is allowed (always let talk to sales be clickable)
  selectedQuantity <= ownedQuantity;
  const upgradeProduct = useAppSelector(state => upgradeProductSelector(state, {
    skuIds: ownedHubSpotCreditsSkuId ? [ownedHubSpotCreditsSkuId] : []
  }));
  const upgradeData = {
    app: APP,
    screen,
    uniqueId: `${trackingSource}-${apiName}`,
    upgradeProduct
  };
  const currencyCode = useDisplayedCurrencyCode();
  const {
    queryParamTerm
  } = useQueryParamTerm();
  const {
    monthlyCalculatedPrice,
    loading: pricingInfoLoading,
    error
  } = useCalculatedPrice({
    apiName,
    queryParamTerm
  });
  const flow = useTrackingFlow({
    productConfigurations,
    purchaseMotion
  });
  if (!ownedHubSpotCreditsSkuId || !includedUnits) return null;
  if (error) {
    throw new CustomError('Failed calculating price', {
      error,
      apiName,
      queryParamTerm
    });
  }
  const handleButtonClick = () => {
    if (isTouchlessPurchase) {
      trackBuyButtonClick({
        purchaseMotion: BUY_TOUCHLESS,
        productNames: apiName ? [apiName] : [],
        ownedProducts,
        flow,
        trackingSource
      });
    } else {
      trackTalkToSalesClick({
        trackingSource,
        productNames: apiName ? [apiName] : [],
        ownedProducts,
        flow
      });
    }
  };
  const handleStepperChange = evt => {
    const input = Math.round(parseInt(evt.target.value, 10) / includedUnits);
    if (isNaN(input)) return;
    const newQuantity = Math.max(minQuantityForStepper, Math.min(maxQuantityForStepper, input));
    trackAdditionalUnitsPicker({
      action: `stepper ${newQuantity > selectedQuantity ? 'increase' : 'decrease'}`,
      trackingSource,
      activeCurrencyCode: currencyCode,
      skuId: ownedHubSpotCreditsSkuId,
      ownedUnitQuantity: ownedQuantity || 0,
      displayedUnitQuantity: selectedQuantity * includedUnits,
      productName: apiName,
      ownedProducts
    });
    setSelectedQuantity(newQuantity);
  };
  const textOverride = isTouchlessPurchase ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "merchCards.breezeTile.buyAdditionalCredits"
  }) : undefined;
  const loading = !isAppReady || pricingInfoLoading;
  if (loading) return /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true
  });
  if (!monthlyCalculatedPrice) return null;
  return /*#__PURE__*/_jsxs(CardWrapper, {
    inlineSize: "100%",
    maxInlineSize: "328px",
    paddingInline: "700",
    paddingBlock: "800",
    display: "flex",
    flexDirection: "column",
    gap: "600",
    "data-test-id": `${apiName}-merch-card`,
    "data-selenium-id": `${apiName}-merch-card`,
    children: [/*#__PURE__*/_jsx(Text, {
      whiteSpace: "nowrap",
      as: "h2",
      typography: "heading-300",
      padding: "0",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "pricingPage.pages.hubspot-credits.info.card.buyMoreCredits"
      })
    }), /*#__PURE__*/_jsxs(CardSection, {
      padding: "0",
      gap: "300",
      display: "flex",
      flexDirection: "column",
      children: [/*#__PURE__*/_jsx(StepperInput, {
        "data-selenium-id": `${trackingSource}-${apiName}-stepper-input`,
        maxInlineSize: "140px",
        stepSize: includedUnits,
        value: selectedQuantity * includedUnits,
        min: minQuantityForStepper * includedUnits,
        max: maxQuantityForStepper * includedUnits,
        onChange: handleStepperChange,
        disabledMinButtonTooltip: ownedQuantity > 0 ? /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "pricingPage.pages.addOns.cards.hubSpotCredits.minTooltipExistingPlan",
          options: {
            count: includedUnits * ownedQuantity
          }
        }) : /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "pricingPage.pages.addOns.cards.hubSpotCredits.minTooltip",
          options: {
            count: includedUnits * ownedQuantity
          }
        }),
        disabledMinButtonTooltipProps: {
          onOpenChange: e => {
            if (e.currentTarget.value) {
              trackHoverInteraction({
                value: getI18nText('pricingPage.pages.addOns.cards.hubSpotCredits.minTooltip', {
                  count: includedUnits * ownedQuantity,
                  locale: 'en-us'
                }),
                trackingSource,
                activeCurrencyCode: currencyCode,
                ownedProducts
              });
            }
          }
        }
      }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: loading,
        heights: [20],
        widths: [75],
        children: /*#__PURE__*/_jsx(Text, {
          typography: "heading-100",
          as: "div",
          children: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "priceDisplay.highlightedPrice.pricePerMonth",
            options: {
              price: /*#__PURE__*/_jsx(PriceDisplay, {
                price: monthlyCalculatedPrice * selectedQuantity,
                currencyCode: currencyCode
              })
            }
          })
        })
      })]
    }), /*#__PURE__*/_jsx(CardSection, {
      padding: "0",
      children: /*#__PURE__*/_jsx(PurchaseMotionButtonWrapper, {
        queryParamTerm: queryParamTerm,
        use: "primary",
        purchaseMotion: purchaseMotion,
        trackingSource: trackingSource,
        upgradeData: upgradeData,
        productConfigurations: productConfigurations,
        disabled: disabledButton,
        onClick: handleButtonClick,
        skipInterstitialPage: true,
        purchaseMotionLocation: purchaseMotionLocation,
        block: true,
        _textOverride: textOverride,
        fallbackSeleniumIdentifier: apiName,
        children: textOverride
      })
    })]
  });
};
export default withErrorBoundary(BuyMoreCredits, {
  fallback: null,
  errorBoundaryName: 'BuyMoreCredits'
});